import { useEffect, useState } from "react";
import { useBeforeUnload, useNavigate, useParams } from "react-router-dom";
import { resumeJurisdiction } from "../../services/common";
import documentService from "../../services/documentService";
import expedientesService from "../../services/expedientesService";
import { useNotification } from "../../context/NotificationContext";

import TextEditorPro from "components/TextEditorPro";

import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Card,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const DocumentEditor = () => {
  let { documentId, id } = useParams();
  documentId = Number(documentId);
  const expedienteId = Number(id);

  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [name, setName] = useState("");
  const [numero, setNumero] = useState("");
  const [anio, setAnio] = useState("");
  const [jurisdiccionBrief, setJurisdiccionBrief] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const leaveMessage = "Hay cambios sin guardar. ¿Está seguro de salir sin guardar?";

  const hasBasePathData = jurisdiccionBrief && numero && anio;
  const basePath = hasBasePathData
    ? {
        title: `${jurisdiccionBrief} ${numero}/${anio}`,
        link: `/file/detail/${expedienteId}`,
      }
    : undefined;

  useBeforeUnload(
    (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = leaveMessage;
      }
    },
    { capture: true }
  );

  useEffect(() => {
    const handleNavigationClick = (event) => {
      if (!isDirty) return;
      const anchor = event.target && event.target.closest ? event.target.closest("a[href]") : null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
      const nextUrl = new URL(anchor.href, window.location.href);
      const sameLocation =
        nextUrl.pathname === window.location.pathname &&
        nextUrl.search === window.location.search &&
        nextUrl.hash === window.location.hash;
      if (sameLocation) return;
      const confirmLeave = window.confirm(leaveMessage);
      if (!confirmLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleNavigationClick, true);
    return () => document.removeEventListener("click", handleNavigationClick, true);
  }, [isDirty, leaveMessage]);

  useEffect(() => {
    async function fetchDocument() {
      try {
        const data = await documentService.getDocumentbyId(documentId);
        setDoc(data || null);
        setEditorContent((data && data.content) || "");
        setIsDirty(false);
        setName((data && data.name) || "");
      } catch {
        showNotification("Error al cargar el documento", "error");
      }
    }

    async function fetchExpedientes() {
      try {
        const data = await expedientesService.getExpedienteById(expedienteId);
        setNumero(data.numero);
        setAnio(data.anio);
        setJurisdiccionBrief(resumeJurisdiction(data.jurisdiccion));
        const jurisdiccion = resumeJurisdiction(data.jurisdiccion);
        const caratula = data.caratula;
        document.title = `${jurisdiccion} ${data.numero}/${data.anio} ${caratula}`;
      } catch {
        showNotification("Error al cargar el expediente", "error");
      }
    }

    fetchDocument();
    fetchExpedientes();
    return () => {
      document.title = "Gestor de Expedientes";
    };
  }, [documentId, expedienteId]);

  const editName = async () => {
    setSubmitting(true);
    try {
      await documentService.updateDocumentNameById({ id: documentId, name });
      setDoc((prev) => (prev ? { ...prev, name } : prev));
      showNotification("Nombre actualizado correctamente", "success");
    } catch {
      showNotification("Error al actualizar el nombre", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteDocument = async () => {
    const shouldDelete = window.confirm("¿Realmente desea eliminar este documento?");
    if (!shouldDelete) return;
    setSubmitting(true);
    try {
      await documentService.deleteDocumentById(documentId);
      showNotification("Documento eliminado correctamente", "success");
      navigate(`/file/detail/${expedienteId}?tab=3`);
    } catch {
      showNotification("Error al eliminar el documento", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (content) => {
    if (!doc) return;
    try {
      await documentService.updateDocumentById({ ...doc, content });
      setDoc((prevDoc) => (prevDoc ? { ...prevDoc, content } : prevDoc));
      setEditorContent(content);
      setIsDirty(false);
      showNotification("Documento guardado correctamente", "success");
    } catch {
      showNotification("Error al guardar el documento", "error");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Editor de documento" basePath={basePath} />

      <Card>
        <MDBox p={1}>
          <Accordion
            style={{ boxShadow: "none" }}
            sx={{
              boxShadow: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.action.hover
                  : "transparent !important",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <MDTypography display="block" variant="button" fontWeight="regular">
                {(doc && doc.name) || ""}
              </MDTypography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox>
                <MDInput
                  fullWidth
                  type="text"
                  label="Nombre"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </MDBox>
            </AccordionDetails>
            <AccordionActions>
              <MDButton variant="gradient" color="success" onClick={editName} disabled={submitting}>
                Guardar
              </MDButton>
              <MDButton variant="gradient" color="error" onClick={deleteDocument} disabled={submitting}>
                Eliminar
              </MDButton>
            </AccordionActions>
          </Accordion>
        </MDBox>
      </Card>
      <MDBox pt={2}>
        {doc && (
          <MDBox sx={{ maxWidth: "210mm", width: "100%", mx: "auto" }}>
            <TextEditorPro
              value={editorContent || ""}
              onChange={setEditorContent}
              onSave={handleSave}
              onDirtyChange={setIsDirty}
              placeholder="Escriba el contenido del documento aquí..."
            />
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default DocumentEditor;
